import { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import { Bell, BriefcaseBusiness } from "lucide-react";
import { useNavigate } from "react-router-dom";

import "./Feed.css";
import { FeedStore } from "./FeedStore";
import { globalData } from "../../globals";
import { AnalyticsClient } from "../../AnalyticsClient/AnalyticsClient";
import { AnalyticsSource } from "../../AnalyticsClient/AnalyticsSources";
import { DelayedLookstyLoader } from "../../GloablComponents/Loader/DelayedLookstyLoader";
import { MasonryGrid } from "../../GloablComponents/Masonry/MasonryGrid";
import { MasonryPostCard } from "../../GloablComponents/Masonry/MasonryPostCard";

export const FeedPage = observer(() => {
  const store = useMemo(() => new FeedStore(), []);
  const navigate = useNavigate();

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    void store.loadFeed();

    AnalyticsClient.track({
      eventType: "feed_opened",
      source: AnalyticsSource.Feed,
    });
  }, [store]);

  useEffect(() => {
    if (!store.HasMore) return;

    const element = loadMoreRef.current;

    if (!element) return;

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          void store.loadMore();
        }
      },
      {
        root: null,
        rootMargin: "300px",
        threshold: 0,
      }
    );

    intersectionObserver.observe(element);

    return () => {
      intersectionObserver.disconnect();
    };
  }, [store, store.HasMore, store.Items.length, store.SelectedCategoryId]);

  return (
    <div className="feed-page">
      <header className="feed-header">
        <div>
          <div className="feed-logo">Looksty</div>

          <h1>Лента идей</h1>
        </div>

        <div className="feed-header-actions">
          {globalData.CurrentUser?.isMaster && (
            <button type="button" className="feed-icon-button" onClick={() => navigate("/master/me")}>
              <BriefcaseBusiness size={22} />
            </button>
          )}

          {globalData.CurrentUser != null && (
            <button type="button" className="feed-icon-button">
              <Bell size={22} />
            </button>
          )}
        </div>
      </header>

      <div className="feed-categories">
        <button type="button" className={store.IsAllCategoriesSelected ? "active" : ""} onClick={() => store.selectCategory(null)}>
          Все
        </button>

        {store.Categories.map((category) => (
          <button type="button" key={category.id} className={store.SelectedCategoryId === category.id ? "active" : ""} onClick={() => store.selectCategory(category.id)}>
            {category.name}
          </button>
        ))}
      </div>

      {store.IsLoading && !store.Items.length && (
        <DelayedLookstyLoader variant="feed" title="Подбираем идеи" subtitle="Собираем свежие работы мастеров" show={store.IsLoading && !store.Items.length} />
      )}

      {store.Error && <div className="feed-message error">{store.Error}</div>}

      <MasonryGrid className="feed-grid">
        {store.Items.map((item, index) => (
          <MasonryPostCard
            key={item.id}
            item={item}
            className="feed-card"
            loading={index < 4 ? "eager" : "lazy"}
            onClick={() => navigate(`/feed/${item.id}`)}

            /*
            В ленте showDistance не передаём,
            поэтому расстояния не будет.
            */
          />
        ))}
      </MasonryGrid>

      {!store.IsLoading && !store.Error && store.Items.length === 0 && <div className="feed-message">В этой категории пока нет работ</div>}

      {store.HasMore && (
        <div ref={loadMoreRef} className="feed-scroll-loader">
          {store.IsLoadingMore ? "Загружаем..." : ""}
        </div>
      )}
    </div>
  );
});
